import React from 'react';
import styled from 'styled-components';

const SafetyPrivacySection = () => {
  const features = [
    { icon: '🛡️', title: 'Child-First Approach', description: 'No personal data collection from children' },
    { icon: '🔒', title: 'Data Minimization', description: 'Only essential information is requested' },
    { icon: '✨', title: 'Transparency', description: 'Clear communication about our practices' },
    { icon: '👨‍👩‍👧‍👦', title: 'Adult-Only Updates', description: 'Communications for 18+ only' }
  ];

  return (
    <Wrapper>
      <ContentContainer>
        <IconHeader>
          <ShieldIcon>🔐</ShieldIcon>
        </IconHeader>
        <Title>Safety & Privacy</Title>
        <Description>
          We do not collect personal information from children. Updates and communications are for adults (18+) only. We maintain a child-first approach to safety, data minimization, and transparency.
        </Description>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>

        <Badge>
          <BadgeIcon>✓</BadgeIcon>
          <BadgeText>COPPA & GDPR Compliant</BadgeText>
        </Badge>
      </ContentContainer>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  width: 100vw;
  background: linear-gradient(120deg, #eaf1fb 0%, #e3eafc 40%, #dbeafe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  padding: 70px 20px;

  @media (max-width: 1024px) {
    padding: 60px 18px;
  }

  @media (max-width: 768px) {
    padding: 50px 16px;
  }

  @media (max-width: 480px) {
    padding: 40px 15px;
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const IconHeader = styled.div`
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const ShieldIcon = styled.div`
  font-size: 2.5rem;
  color: #2563eb;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Title = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 25px 0;
  background: linear-gradient(90deg, #1e3a8a 0%, #2563eb 60%, #60a5fa 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  text-align: center;

  @media (max-width: 1024px) {
    font-size: 2.4rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
    margin-bottom: 16px;
  }
`;

const Description = styled.p`
  font-size: 1.15rem;
  color: #334155;
  font-weight: 400;
  line-height: 1.8;
  margin: 0 auto 40px auto;
  text-align: center;
  max-width: 900px;
  padding: 0 20px;
  font-style: italic;

  @media (max-width: 1024px) {
    font-size: 1.08rem;
    max-width: 800px;
  }

  @media (max-width: 768px) {
    font-size: 1.02rem;
    margin-bottom: 32px;
    line-height: 1.7;
  }

  @media (max-width: 480px) {
    font-size: 0.96rem;
    margin-bottom: 28px;
    padding: 0 16px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-bottom: 32px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;
    max-width: 350px;
  }
`;

const FeatureCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.12);
  transition: all 0.3s ease;
  border: 1px solid rgba(30, 64, 175, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(30, 64, 175, 0.2);
  }

  @media (max-width: 768px) {
    padding: 18px 16px;
  }

  @media (max-width: 480px) {
    padding: 16px 14px;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const FeatureTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: 0.85rem;
  color: #64748b;
  text-align: center;
  margin: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.78rem;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  padding: 12px 28px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(30, 64, 175, 0.15);
  border: 2px solid rgba(37, 99, 235, 0.3);

  @media (max-width: 768px) {
    padding: 10px 24px;
    gap: 8px;
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    gap: 8px;
  }
`;

const BadgeIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb 0%, #60a5fa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.9rem;
  font-weight: 900;

  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
    font-size: 0.8rem;
  }
`;

const BadgeText = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e3a8a;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

export default SafetyPrivacySection;
